import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Clock } from 'lucide-react';
import { addMinutes, isBefore, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ScheduleSendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSchedule: (date: Date) => void;
}

export function ScheduleSendDialog({ open, onOpenChange, onSchedule }: ScheduleSendDialogProps) {
  const now = new Date();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    addMinutes(now, 60)
  );
  const [time, setTime] = useState('08:00');

  const minDate = startOfDay(now);

  const handleConfirm = () => {
    if (!selectedDate) return;
    const [h, m] = time.split(':').map(Number);
    const scheduled = new Date(selectedDate);
    scheduled.setHours(h, m, 0, 0);

    if (isBefore(scheduled, addMinutes(now, 5))) {
      return; // Minimum 5 minutes in the future
    }

    onSchedule(scheduled);
    onOpenChange(false);
  };

  const getScheduledDate = () => {
    if (!selectedDate) return null;
    const [h, m] = time.split(':').map(Number);
    const d = new Date(selectedDate);
    d.setHours(h, m, 0, 0);
    return d;
  };

  const isValid = () => {
    const d = getScheduledDate();
    return d && !isBefore(d, addMinutes(now, 5));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm p-0 gap-0">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Clock className="h-4 w-4 text-primary" />
            Agendar envio
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 pb-2">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            disabled={(date) => isBefore(date, minDate)}
            locale={ptBR}
            className="rounded-md border mx-auto"
          />

          <div className="mt-3 space-y-1.5">
            <Label htmlFor="schedule-time" className="text-sm font-medium">
              Horário
            </Label>
            <Input
              id="schedule-time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full"
            />
          </div>

          {!isValid() && selectedDate && (
            <p className="text-xs text-destructive mt-1.5">
              O horário deve ser pelo menos 5 minutos no futuro.
            </p>
          )}
        </div>

        <DialogFooter className="px-4 py-3 border-t">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button size="sm" onClick={handleConfirm} disabled={!isValid()}>
            Agendar envio
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
